import { EdgeLabelRenderer, getBezierPath, type Position } from "@xyflow/react";
import { type MouseEvent,useCallback } from "react";

import { type AddNodeMenuOpenPayload,useAddNodeMenu } from "../nodes/add-node-menu-context";
import { AddHandleChrome } from "../nodes/AddHandleChrome";
import { EdgeAddButton, HANDLE_SIZE } from "../nodes/styled/basehandle.styled";
import type { RuleEdgeData, RuleEdgeProps } from "../types/edges";
import { getBezierPointAtT } from "./bezierEdgeUtils";

const PILL_WIDTH = 60;
const PILL_HEIGHT = 24;
const HALF_PILL_WIDTH = PILL_WIDTH / 2;
const HALF_PILL_HEIGHT = PILL_HEIGHT / 2;

const ADD_BUTTON_SIZE = HANDLE_SIZE;

/** Distance from the target along the edge chord to leave before the arrow marker (marker is ~20px). */
const ADD_BUTTON_MIN_CLEAR_BEFORE_MARKER = 32;

// QA fix (E1): unified gray for edge strokes + arrow markers.
// Kept as the MarkerType.Arrow `color` so React Flow's marker-id generation
// stays stable (it keys markers off this string); the arrowhead is repainted
// to the gradient in canvas.styled via the .react-flow__arrowhead rule.
export const DEFAULT_EDGE_COLOR = "#7E848C";

// Solid CoApp brand teal for the marching-ant edge strokes. NOTE: an SVG
// objectBoundingBox gradient (url(#coapp-grad-primary)) is NOT rendered when the
// shape's bounding box has zero width/height — which is exactly what a straight
// vertical edge (node stacked above node) produces — so gradient strokes made
// those edges invisible. A solid color always paints. The gradient still lives
// on the arrowhead (a triangle with a non-degenerate bbox).
export const EDGE_STROKE = "#02D5AB";

export type EdgeAddButtonLayerProps = {
  id: string;
  source: string;
  target: string;
  sourceHandleId?: string | null;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition?: Position;
  targetPosition?: Position;
  data?: RuleEdgeProps["data"];
  /**
   * Skips getBezierPath placement (use for a custom path shape on the same line).
   */
  addButtonScreenPosition?: { x: number; y: number };
};

/**
 * Renders the + on the edge: midpoint when unlabeled; with a label, below the pill by default, or
 * between pill and arrow when `addButtonBetweenPillAndMarker` is set (decision true/false edges).
 */
export function EdgeAddButtonLayer({
	id,
	source,
	target,
	sourceHandleId,
	sourceX,
	sourceY,
	targetX,
	targetY,
	sourcePosition,
	targetPosition,
	data,
	addButtonScreenPosition,
}: EdgeAddButtonLayerProps) {
	const { openMenu } = useAddNodeMenu();
	const edgeData = data as RuleEdgeData | undefined;

	const handleAddClick = useCallback(
		(event: MouseEvent<HTMLDivElement>) => {
			event.stopPropagation();
			if (event.currentTarget instanceof HTMLElement) {
				const menuPayload: AddNodeMenuOpenPayload = {
					sourceNodeId: (edgeData?.addButtonSourceNodeId as string | undefined) ?? source,
					sourceHandleId:
            edgeData?.addButtonSourceHandleId !== undefined
            	? (edgeData.addButtonSourceHandleId as string | null)
            	: (sourceHandleId ?? null),
					targetNodeId: (edgeData?.addButtonTargetNodeId as string | undefined) ?? target,
					edgeId: (edgeData?.addButtonEdgeId as string | undefined) ?? id,
				};
				openMenu(event.currentTarget, menuPayload);
			}
		},
		[openMenu, source, sourceHandleId, target, id, edgeData],
	);

	if (addButtonScreenPosition) {
		return (
			<EdgeLabelRenderer>
				<EdgeAddButton
					className="nopan nodrag"
					onClick={handleAddClick}
					style={{
						position: "absolute",
						transform: `translate(-50%, -50%) translate(${addButtonScreenPosition.x}px, ${addButtonScreenPosition.y}px)`,
					}}
				>
					<AddHandleChrome />
				</EdgeAddButton>
			</EdgeLabelRenderer>
		);
	}

	const chordLen = Math.hypot(targetX - sourceX, targetY - sourceY) || 1;

	const [, pathLabelX, pathLabelY] = getBezierPath({
		sourceX,
		sourceY,
		sourcePosition,
		targetX,
		targetY,
		targetPosition,
	});

	const curve = {
		sourceX,
		sourceY,
		targetX,
		targetY,
		sourcePosition,
		targetPosition,
	};

	// Anchor the add-button at the edge midpoint so it sits an equal distance
	// from both nodes. (Previously t≈0.25, biased toward the source; per design
	// the + should be centered between the two nodes.) Labeled / between-pill
	// placements keep their bespoke logic.
	const DEFAULT_ADD_BUTTON_T = 0.5;
	let [addButtonX, addButtonY] = getBezierPointAtT(DEFAULT_ADD_BUTTON_T, curve);

	if (edgeData?.addButtonBetweenPillAndMarker) {
		/** Map chord-distance bounds to approximate `t` on the Bézier, then sample the curve. */
		const wantMinDist = 0.5 * chordLen + HALF_PILL_WIDTH + 10 + ADD_BUTTON_SIZE / 2;
		const wantMaxDist = chordLen - ADD_BUTTON_MIN_CLEAR_BEFORE_MARKER;
		let tMin = wantMinDist / chordLen;
		let tMax = wantMaxDist / chordLen;
		tMin = Math.max(0.02, Math.min(tMin, 0.98));
		tMax = Math.max(0.02, Math.min(tMax, 0.98));
		const t = tMin <= tMax ? (tMin + tMax) / 2 : tMax;
		[addButtonX, addButtonY] = getBezierPointAtT(t, curve);
	} else if (edgeData?.label) {
		/** Past the path center toward the target, staying on the stroke (replaces screen-Y “below pill”). */
		const deltaT = (HALF_PILL_HEIGHT + 10 + ADD_BUTTON_SIZE / 2) / (chordLen * 1.75);
		const t = Math.min(0.5 + deltaT, 0.95);
		[addButtonX, addButtonY] = getBezierPointAtT(t, curve);
	}
	// Suppress unused-vars: pathLabelX/Y are still computed above so callers
	// of getBezierPath downstream don't break; we just don't need them now.
	void pathLabelX; void pathLabelY;

	return (
		<EdgeLabelRenderer>
			<EdgeAddButton
				className="nopan nodrag"
				onClick={handleAddClick}
				style={{
					position: "absolute",
					transform: `translate(-50%, -50%) translate(${addButtonX}px, ${addButtonY}px)`,
				}}
			>
				<AddHandleChrome />
			</EdgeAddButton>
		</EdgeLabelRenderer>
	);
}

const BaseEdge = ({
	id,
	source,
	target,
	sourceHandleId,
	sourceX,
	sourceY,
	targetX,
	targetY,
	sourcePosition,
	targetPosition,
	markerEnd,
	data,
	style = {},
}: RuleEdgeProps) => {
	const [edgePath, labelX, labelY] = getBezierPath({
		sourceX,
		sourceY,
		sourcePosition,
		targetX,
		targetY,
		targetPosition,
	});

	const pillColor = data?.color ?? "#666666";

	return (
		<>
			<path
				id={id}
				// QA fix (E1/E2): adopt the shared `.react-flow__edge-path` class so
				// custom edges inherit the subtle gray marching-ant animation defined
				// in canvas.styled. Inline stroke/width below still win for color +
				// thickness; rounded linejoin softens the bends.
				className="react-flow__edge-path"
				d={edgePath}
				style={{
					stroke: EDGE_STROKE,
					strokeWidth: 2.5,
					strokeLinejoin: "round",
					strokeLinecap: "round",
					fill: "none",
					...style,
				}}
				markerEnd={markerEnd}
			/>

			{data?.label && (
				<g
					transform={`translate(${labelX}, ${labelY})`}
					style={{ pointerEvents: "none", userSelect: "none" }}
				>
					<rect
						x={-HALF_PILL_WIDTH}
						y={-HALF_PILL_HEIGHT}
						width={PILL_WIDTH}
						height={PILL_HEIGHT}
						rx={6}
						ry={6}
						fill="#fff"
					/>
					<rect
						x={-HALF_PILL_WIDTH}
						y={-HALF_PILL_HEIGHT}
						width={PILL_WIDTH}
						height={PILL_HEIGHT}
						rx={6}
						ry={6}
						fill={pillColor}
						fillOpacity={0.12}
						stroke={pillColor}
						strokeWidth={1.5}
					/>

					<text
						textAnchor="middle"
						dominantBaseline="middle"
						style={{
							fontSize: "0.75rem",
							fontWeight: 400,
							fill: pillColor,
							fontFamily: "Roboto, sans-serif",
						}}
					>
						{data.label}
					</text>
				</g>
			)}

			{!data?.disableAddButton && (
				<EdgeAddButtonLayer
					id={id}
					source={source}
					target={target}
					sourceHandleId={sourceHandleId}
					sourceX={sourceX}
					sourceY={sourceY}
					targetX={targetX}
					targetY={targetY}
					sourcePosition={sourcePosition}
					targetPosition={targetPosition}
					data={data}
				/>
			)}
		</>
	);
};

export default BaseEdge;
